import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Theme } from '@mui/material';
import { TMainColor } from 'components/ColorButton/ColorButton';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { TFunction } from 'i18next';
import { TSessionConfigData } from 'providers/contexts/CosmosContext';
import { Workflow, WorkflowProcess } from 'providers/contexts/extensions/Workflows';
import { formatAssetString } from 'utility/functions';
import {
  checkModifyAssetsPermission,
  checkSiteAdminPermission,
} from 'utility/permissions';
import { ApiErrorResponse, FullEditorialAsset } from 'utility/types/dataProvider';
import { SaveResponse } from '../context/AssetSaveContextProvider';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(LocalizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(isSameOrAfter);

/**
 * RightSide - trySaveAssetAsync error msg helper
 * Extracts ApiErrorResponse / SaveResponse
 * error message or uses fallback string
 */
export function getErrorMessage(
  error: ApiErrorResponse | SaveResponse | Error | string | any,
  fallback: string,
): string {
  // Handle null/undefined error argument
  if (!error) {
    return fallback;
  }

  // Defensive code for object checks
  if (Array.isArray(error)) {
    console.warn('[getErrorMessage] Invalid argument:', error);
    return fallback;
  }

  // Try ApiErrorResponse / SaveResponse properties
  // first, should come in as a generic object

  // Try message property (most common)
  if (error?.message) {
    return String(error.message);
  }

  // Try axios message (eg network errors)
  if (error?.axiosMessage) {
    return String(error.axiosMessage);
  }

  // Handle JS standard Error objects
  if (error instanceof Error && error?.message) {
    return String(error.message);
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Return fallback
  return fallback;
}

/**
 * Normalizes no_publish to boolean.
 * Anything falsy consider no_publish.
 * eg: 0 / false / null / undefined / '', etc.
 * https://developer.mozilla.org/en-US/docs/Glossary/Falsy
 *
 * Note the API has another status code number "2"
 * for no_publish when by system background processing.
 * eg: a video asset is being processed...
 * https://git.sys1.vip.inn.mwn.leeent.net/projects/DEV/repos/tncms/browse/lib/tncms/asset.php
 *
 */
export const normalizeNoPublish = (value: any): boolean => {
  const isNoPublish = Boolean(value);
  return isNoPublish;
};

/**
 * CMSADMIN-1080 Requirement:
 * - The Asset Stats tab should only be enabled
 *   when the current time is past the assets start date.
 * Ticket comments:
 *   "Detecting whether or not an asset was ever published is problematic.
 *   The simplest option that can be offered is that if an assets start date has not been started that
 *   nothing will be shown. I would also suggest that the start date of an asset is
 *   actually a mandatory lower bound for the start of any reporting since, in theory,
 *   it should have no traffic before that time."
 */
export const getAssetStatsPublishedCheck = (record?: Partial<FullEditorialAsset>) => {
  // Guard against undefined record
  if (!record) {
    return false;
  }

  const draft = record?.draft;
  const start_time = record?.start_time;
  const properties = record?.properties;

  const isNoPublish = normalizeNoPublish(properties?.no_publish);

  const hasValidStartTime = start_time
    ? dayjs.utc().isSameOrAfter(dayjs.utc(start_time))
    : false;

  const existsAndNotDraft = Boolean(record?.update_time && !draft);

  const statsCheck = (hasValidStartTime || existsAndNotDraft) && !isNoPublish;
  return statsCheck;
};

/**
 * Build a localized asset update toast string to display
 * based on the asset data returned by the API after saving.
 * [status]: "Status is draft and published."
 * [action, status]: "Article updated, status is draft and published."
 */

type TPubKey = 'published' | 'unpublished';
type TDraftKey = 'live' | 'draft';
type TLookupKey = `${TPubKey}-${TDraftKey}`;
type TStatusLUT = Record<TLookupKey, string>;

type BuildStatusToastMsgArgs = {
  /** Translation function */
  t: TFunction;
  /** Asset data as provided by save API response.*/
  resultAsset?: Partial<FullEditorialAsset>;
  /**
   * If true, returns: "{{Type}} {{status}}"
   * eg: Article status is draft and published.
   */
  withType?: boolean;
  /**
   * If true, returns: "{{type}} [created/updated], {{status}}"
   * eg: "Article updated, status is draft and published."
   */
  withAction?: boolean;
  /**
   * Determines created/updated verb for withAction.
   * */
  isEditing?: boolean;
  /**
   * If True, returns "{{type}} autosaved, {{status}}"
   */
  isAutosave?: boolean;
};

/**
 * Build status message based on API response.
 *
 * Examples:
 * Default: "Status is draft and unpublished."
 * withType: "Article status is draft and unpublished."
 * withAction and isEditing: "Article updated, status is draft and unpublished."
 * withAction and !isEditing: "Article created, status is draft and unpublished."
 */

export const buildStatusToastMsg = ({
  t,
  resultAsset,
  withType = false,
  withAction = false,
  isEditing = false,
  isAutosave = false,
}: BuildStatusToastMsgArgs): string => {
  // Build lookup key based on assets information
  const assetType = String(resultAsset?.type);
  const isDraft = Boolean(resultAsset?.draft);

  const isNoPublish = normalizeNoPublish(resultAsset?.properties?.no_publish);
  const pubStatus: TPubKey = isNoPublish ? 'unpublished' : 'published';
  const draftStatus: TDraftKey = isDraft ? 'draft' : 'live';
  const statusKey: TLookupKey = `${pubStatus}-${draftStatus}`;

  // Translation strings
  const assetTypeStr = formatAssetString(assetType, t);
  const strCreated = t('blox.editorial.asset.created', { type: assetTypeStr });
  const strUpdated = t('blox.editorial.asset.updated', { type: assetTypeStr });
  const strTypeAutosaved = t('blox.autosave.type_autosaved', { type: assetTypeStr });
  const strPublishedLive = t('blox.editorial.asset.published_live');
  const strPublishedDraft = t('blox.editorial.asset.published_draft');
  const strUnpublishedLive = t('blox.editorial.asset.unpublished_live');
  const strUnpublishedDraft = t('blox.editorial.asset.unpublished_draft');

  // Lookup table for possible combinations
  const msgLut: TStatusLUT = {
    'published-live': strPublishedLive,
    'published-draft': strPublishedDraft,
    'unpublished-live': strUnpublishedLive,
    'unpublished-draft': strUnpublishedDraft,
  };

  const statusMsg = msgLut[statusKey] ?? '';

  // Prepend asset type to status message
  // eg: "Article status is draft and unpublished."
  if (withType) {
    const lowerStatus = statusMsg.toLocaleLowerCase();
    return `${assetTypeStr} ${lowerStatus}`;
  }

  // Prepend action verb and return early.
  // eg: "Article updated, status is draft and unpublished."
  if (withAction) {
    const actionMsg = isEditing ? strUpdated : strCreated;
    const lowerStatus = statusMsg.toLocaleLowerCase();
    return `${actionMsg}, ${lowerStatus}`;
  }

  // Edge case for autosave
  // eg: "Article autosaved, status is draft and unpublished."
  if (isAutosave) {
    const lowerStatus = statusMsg.toLocaleLowerCase();
    const autosaveMsg = `${strTypeAutosaved}, ${lowerStatus}`;
    return autosaveMsg;
  }

  // Return base status message
  // eg: "Status is draft and unpublished."
  return statusMsg;
};

/**
 * CMSADMIN-2180: Workflow Process Color Coding
 *
 * Notes: force_live / force_draft are mutex
 * This is what we are seeing in the Blue UI
 *
 * Legend (process flags)
 * - RFP: Ready for print
 * - RFW: Ready for web
 * - FD:  Force draft
 * - FL:  Force live
 * - ROD: Return on draft
 *
 * +----------------------+-----------+-------+-----+-------+------+
 * | Process              | Syndicate | Print | Web | Draft | Live |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFW                  | yes       |       | yes |       |      |
 * +----------------------+-----------+-------+-----+-------+------+
 * | FD                   | yes       |       |     | yes   |      |
 * +----------------------+-----------+-------+-----+-------+------+
 * | FL                   | yes       |       |     |       | yes  |
 * +----------------------+-----------+-------+-----+-------+------+
 * | ROD (*)              | yes       |       |     |       |      |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFP (*)              | yes       | yes   |     |       |      |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFP + RFW            | yes       | yes   | yes |       |      |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFW + FL             | yes       |       | yes |       | yes  |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFP + FL             | yes       | yes   |     |       | yes  |
 * +----------------------+-----------+-------+-----+-------+------+
 * | ROD + FL             | yes       |       |     |       | yes  |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFW + FD             | yes       |       | yes | yes   |      |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFW + ROD            | yes       |       | yes |       |      |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFP + FD             | yes       | yes   |     | yes   |      |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFP + ROD (*)        | yes       | yes   |     |       |      |
 * +----------------------+-----------+-------+-----+-------+------+
 * | ROD + FD             | yes       |       |     | yes   |      |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFW + ROD + FD       | yes       |       | yes | yes   |      |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFW + ROD + FL       | yes       |       | yes |       | yes  |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFP + RFW + FL       | yes       | yes   | yes |       | yes  |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFP + RFW + FD       | yes       | yes   | yes | yes   |      |
 * +----------------------+-----------+-------+-----+-------+------+
 * | RFP + RFW + ROD + FL | yes       | yes   | yes |       | yes  |
 * +----------------------+-----------+-------+-----+-------+------+
 *
 * Process Object Example:
 *  {
 *    "id": "ebc85059-f1ad-49d0-91ea-c64a7875408f",
 *    "name": "RFW",
 *    "group": "Editorial Modify",
 *    "print_ready": false,
 *    "web_ready": true,
 *    "force_draft": false,
 *    "force_live": false,
 *    "return_on_draft": false,
 *    "no_syndicate": false,
 *    "reset_start_time": false,
 *    "disable_collaboration": false,
 *    "site_tags": []
 *  }
 *
 *
 * CMSADMIN-2180 #nxt_save_asset_rework
 *
 * Note the behaviour in some cases:
 *   - Asset: Status: Live, Published: Yes
 *   - User changes to process without RFW
 *   - Now: Status: Live, Published: No
 *
 * Those cases are marked with an asterisk above.
 * Specifically seems related to processes where
 * certain flags are not explicitly set. Therefore
 * absence of the flag seems to "unpublish" it..
 *
 * Effectively seems to be:
 * - Without explicit web_ready flag: "unpublishes".
 * - Draft/Live: only changed by force_live or force_draft
 *
 * This also can be miscommunicating asset state
 * to the end user, because nothing is actually
 * pushed "live" to the frontend while the asset is
 * being saved in draft status.
 *
 * After a few meetings about this color coding and
 * the "P/D and publish" verbiage, team is leaning
 * towards removing all of the color coding for
 * process/workflows and all "publish" verbiage.
 *
 * The only true way to know for certain that something
 * results in draft, off process object data alone
 * is "force_draft", and the "published" moniker is a
 * combination of both "force_live" and "web_ready" flags
 * ... AND this is making zero assumptions about any kind
 * of automations a client may have setup on their site.
 *
 * Example:
 * - Asset currently: Live and Not Published (main save: blue)
 * - User selects process - has just RFW flag
 * - Result: Live and Published (main: Green)
 * >> NOTE: The asset was "published" because it remained
 * "live" and recieved the "ready for web" flag.
 *
 * Example:
 * - Asset currently: Draft and Not Published (main save: orange)
 * - User selects process - has just RFW flag
 * - Result is "Draft and Published" (main: orange)
 * >> NOTE: The draft status was not changed. The state is
 * miscommunicated to the user as "published" because the
 * asset remained in draft status. Until this asset goes to
 * "live" status, it will remain inaccessible on frontend.
 *
 * Example:
 *  - Asset currently: Live and Published (main save: green)
 *  - User selects process with just RFP
 *  - Result: Live and Not Published (main: blue)
 * >> NOTE: The live status was unchanged. Because the selected
 * process did not explicitly have RFW, it was unpublished.
 * As changes were saved "live", the asset is correctly not published.
 *
 *
 */

/**
 * Draft = orange/warning
 * Publish = green/success
 * Fallback = blue/primary
 */

type GetWfProcessInfoArgs = {
  t: TFunction;
  theme: Theme;
  process: WorkflowProcess | null;
  hasNewSaveBeta: boolean;
};

type GetWfProcessInfoResult = {
  willDraft: boolean;
  willPublish: boolean;
  saveText: string;
  mainColor: TMainColor;
};

export const getWfProcessInfo = ({
  t,
  theme,
  process,
  hasNewSaveBeta = false,
}: GetWfProcessInfoArgs): GetWfProcessInfoResult => {
  // Translation strings
  const strSaveChanges = t('blox.actions.save_changes');
  const strSavePublish = t('blox.actions.save_publish');
  // const strSaveDraft = t('blox.actions.save_draft');

  // Default fallback for invalid input
  if (!process) {
    return {
      willPublish: false,
      willDraft: false,
      mainColor: theme.palette.primary.main,
      saveText: strSaveChanges,
    };
  }

  // Extract process flags
  const hasForceDraft = Boolean(process?.force_draft);
  const hasForceLive = Boolean(process?.force_live);
  const hasWebReady = Boolean(process?.web_ready);

  // CMSADMIN-2180 #nxt_save_asset_rework
  // older (incorrect) color coding for
  // RFW "publish" for non-beta users.
  if (!hasNewSaveBeta) {
    return {
      willPublish: hasWebReady,
      willDraft: hasForceDraft,
      mainColor: hasWebReady ? theme.palette.success.main : theme.palette.primary.main,
      saveText: hasWebReady ? strSavePublish : strSaveChanges,
    };
  }

  // Try to determine next state via process data
  // FD - for certain results in draft
  // FL & RFW - for certain results in "publish"
  const willDraft = hasForceDraft;
  const willPublish = hasForceLive && hasWebReady;

  // CMSADMIN-2180 #nxt_save_asset_rework
  // Removing requested color logic as this
  // may not be accurate in workflows
  // const muiColor = willPublish ? 'success' : willDraft ? 'warning' : 'primary';
  // const actionTxt = willPublish ? strSavePublish : willDraft ? strSaveDraft : strSaveChanges;

  return {
    willPublish: willPublish,
    willDraft: willDraft,
    mainColor: theme.palette.primary.main,
    saveText: strSaveChanges,
  };
};

/**
 * Compute Workflow Process Information
 * which is relative to the current workflow.
 */
type GetWfProcessTargetInfoArgs = {
  t: TFunction;
  theme: Theme;
  currWorkflow: Workflow | null;
  currProcess: WorkflowProcess | null;
  targetProcess: WorkflowProcess | null;
  hasNewSaveBeta: boolean;
};

export type ProcessTargetInfo = {
  process: WorkflowProcess | null; // resolved target process by its id
  actionText?: string; // "Promote to [Process]" or "Demote to [Process]"
  confirmText?: string; // "Confirm Promote" or "Confirm Demote"
  isPromotion?: boolean; // target is higher index than current?
  isDemotion?: boolean; // target is lower index than current?
  isCurrent?: boolean; // target is the same as current?
  isAdjacent?: boolean; // target is +1/-1 step relative to current?
  isFirstProcess?: boolean; // target is the first in the workflow?
  isLastProcess?: boolean; // target is the last in the workflow?
  // via getWfProcessInfo:
  mainColor: TMainColor; // process color code css
  saveText?: string; // "Save Draft/Publish/Changes" - based on process flags
  willPublish?: boolean; // target should result in "live and published"?
  willDraft?: boolean; // target should result in "draft"?
};

export const getWfProcessTargetInfo = ({
  t,
  theme,
  currWorkflow,
  currProcess,
  targetProcess,
  hasNewSaveBeta = false,
}: GetWfProcessTargetInfoArgs): ProcessTargetInfo => {
  // Return default fallback for invalid input
  if (!currWorkflow || !currProcess || !targetProcess) {
    return {
      process: null,
      mainColor: theme.palette.primary.main,
    };
  }

  // Find and confirm the target is within workflow
  const tgtProc =
    currWorkflow.processes?.find((p) => p?.id === targetProcess?.id) ?? null;

  // Determine its position relative to the current process
  const currProcIdx =
    currWorkflow.processes?.findIndex((p) => p?.id === currProcess?.id) ?? -1;

  // If the new process is not found or current process index is invalid, return null and false
  if (!tgtProc || currProcIdx === -1) {
    return {
      process: null,
      mainColor: theme.palette.primary.main,
    };
  }

  // Translation strings
  const strSaveChanges = t('blox.actions.save_changes');
  const strCurrent = t('blox.general.current');
  const strPromoteTo = t('blox.editorial.asset.promote_to');
  const strDemoteTo = t('blox.editorial.asset.demote_to');
  const strConfirmPromotion = t('blox.editorial.asset.confirm_promotion');
  const strConfirmDemotion = t('blox.editorial.asset.confirm_demotion');
  const strPromotePublishTo = t('blox.editorial.asset.promote_and_publish_to');
  const strDemotePublishTo = t('blox.editorial.asset.demote_and_publish_to');

  // Determine promote or demote based on position workflow
  const tgtProcIdx =
    currWorkflow.processes?.findIndex((p) => p?.id === tgtProc?.id) ?? -1;
  const isPromotion = tgtProcIdx > currProcIdx;
  const isDemotion = tgtProcIdx < currProcIdx;
  const isCurrent = tgtProcIdx === currProcIdx;

  // Any user can promote/demote to adjacent processes
  // otherwise needs change process permission to jump multiple steps
  // on top of any other perms (can modify assets, not read-only mode, etc.)
  const isAdjacent = tgtProcIdx !== -1 && Math.abs(tgtProcIdx - currProcIdx) === 1;
  const isFirstProcess = tgtProcIdx === 0;
  const isLastProcess = tgtProcIdx === (currWorkflow.processes?.length ?? 0) - 1;

  // Collect relevant properties for the target process itself
  const { willPublish, willDraft, mainColor, saveText } = getWfProcessInfo({
    t: t,
    theme: theme,
    process: tgtProc,
    hasNewSaveBeta: hasNewSaveBeta,
  });

  const processName = String(tgtProc?.name);

  /**
   * CMSADMIN-2180 #nxt_save_asset_rework
   * Action text
   *
   * BETA:
   * Simply "Promote to [Process]" or "Demote to [Process]"
   * Do not make any presumptions about publish status.
   *
   * NON-BETA:
   * "Promote to [Process]" or "Promote and Publish to [Process]"
   * "Demote to [Process]" or "Demote and Publish to [Process]"
   */

  let actionText = '';
  if (isCurrent) {
    actionText = `${strCurrent}: ${processName}`;
  } else {
    const withPublishVerb = !hasNewSaveBeta && willPublish;
    const promotePrefix = withPublishVerb ? strPromotePublishTo : strPromoteTo;
    const demotePrefix = withPublishVerb ? strDemotePublishTo : strDemoteTo;
    actionText = isPromotion
      ? `${promotePrefix} ${processName}`
      : `${demotePrefix} ${processName}`;
  }

  /**
   * Beta Usage Only
   * Action button text for Modal
   * "Confirm Promotion" or "Confirm Demotion"
   */
  let confirmText = '';
  if (isPromotion) {
    confirmText = strConfirmPromotion;
  } else if (isDemotion) {
    confirmText = strConfirmDemotion;
  } else {
    confirmText = strSaveChanges;
  }

  return {
    process: tgtProc,
    isPromotion: isPromotion,
    isCurrent: isCurrent,
    isDemotion: isDemotion,
    isAdjacent: isAdjacent,
    isFirstProcess: isFirstProcess,
    isLastProcess: isLastProcess,
    confirmText: confirmText,
    actionText: actionText,
    // via getWfProcessInfo:
    mainColor: mainColor,
    saveText: saveText,
    willPublish: willPublish,
    willDraft: willDraft,
  };
};

/**
 * Helper to access adjacent process information
 * Pass current workflow and process from context.
 */

type AdjacentProcessInfo = {
  currentProcessIndex: number;
  promoteProcess: WorkflowProcess | null;
  demoteProcess: WorkflowProcess | null;
  currentProcess: WorkflowProcess | null;
};

export function getAdjacentProcesses(
  currentWorkflow: Workflow | null,
  currentWorkflowProcess: WorkflowProcess | null,
): AdjacentProcessInfo {
  // Early return if no workflow or processes
  if (!Array.isArray(currentWorkflow?.processes) || !currentWorkflow?.processes?.length) {
    return {
      currentProcessIndex: -1,
      promoteProcess: null,
      demoteProcess: null,
      currentProcess: null,
    };
  }

  // Find current process index
  const currentProcessIndex = currentWorkflow.processes.findIndex(
    (p) => p?.id === currentWorkflowProcess?.id,
  );

  // No current process found
  if (currentProcessIndex === -1) {
    return {
      currentProcessIndex: -1,
      promoteProcess: null,
      demoteProcess: null,
      currentProcess: null,
    };
  }

  // Compute demote process (-1 from current)
  const demoteProcess =
    currentProcessIndex > 0
      ? (currentWorkflow.processes[currentProcessIndex - 1] ?? null)
      : null;

  // Compute promote process (+1 from current)
  const promoteProcess =
    currentProcessIndex < currentWorkflow.processes.length - 1
      ? (currentWorkflow.processes[currentProcessIndex + 1] ?? null)
      : null;

  // Convenience return of current process
  const currentProcess = currentWorkflow.processes[currentProcessIndex] ?? null;

  return {
    currentProcessIndex,
    promoteProcess,
    demoteProcess,
    currentProcess,
  };
}

/**
 * Search backwards through the workflow processes to find
 * the first process that has the return_on_draft property true.
 *
 * Returns the prior process with return_on_draft.
 * Returns null if not found or invalid input args.
 */

type FindReturnOnDraftProcessArgs = {
  currentWorkflow: Workflow | null;
  currentProcess: WorkflowProcess | null;
};

type FindReturnOnDraftProcessResult = WorkflowProcess | null;

export function findReturnOnDraftProcess({
  currentWorkflow,
  currentProcess,
}: FindReturnOnDraftProcessArgs): FindReturnOnDraftProcessResult {
  // Safeguards
  if (!currentProcess) {
    return null;
  }
  if (!Array.isArray(currentWorkflow?.processes)) {
    return null;
  }
  if (!currentWorkflow?.processes?.length) {
    return null;
  }

  // Find current process index
  const currentProcessIndex = currentWorkflow.processes.findIndex((p) => {
    return p?.id === currentProcess?.id;
  });

  // Current process not found
  if (currentProcessIndex === -1) {
    return null;
  }

  // Get processes before current and search backward
  const priorProcesses = currentWorkflow.processes.slice(0, currentProcessIndex);
  const returnOnDraftProcess = priorProcesses.findLast((p) => {
    return Boolean(p?.return_on_draft);
  });

  const result = returnOnDraftProcess ?? null;
  return result;
}

/**
 * Get primary save button label and color based
 * on the current normalized form state for
 * properties.no_publish and draft values.
 *
 * CMSADMIN-2180 #nxt_save_asset_rework
 *
 * BETA:
 * - Draft mode: "Save Draft" (warning/orange)
 * - Publish live mode: "Save and Publish" (success/green)
 * - Other/Default: "Save Changes" (primary/blue)
 *
 * NON-BETA:
 * Simply "Save" for all states, with primary/blue color.
 * Quick save action always tries to set draft to payload.
 */

type GetSaveLabelAndColorArgs = {
  t: TFunction;
  theme: Theme;
  isDraft: boolean;
  isNoPublish: boolean;
  hasNewSaveBeta: boolean;
  isTemplate?: boolean;
};

type SaveLabelAndColor = {
  label: string;
  mainColor: TMainColor;
};

export function getSaveLabelAndColor({
  t,
  theme,
  isDraft,
  isNoPublish,
  hasNewSaveBeta = false,
  isTemplate = false,
}: GetSaveLabelAndColorArgs): SaveLabelAndColor {
  const strSave = t('blox.actions.save');
  const strSaveDraft = t('blox.actions.save_draft');
  const strSavePublish = t('blox.actions.save_publish');
  const strSaveChanges = t('blox.actions.save_changes');

  // Templates have no draft/publish lifecycle: always "Save", primary color.
  if (isTemplate) {
    return {
      label: strSave,
      mainColor: theme.palette.primary.main,
    };
  }

  // Not in beta - default mode
  if (!hasNewSaveBeta) {
    return {
      label: strSave,
      mainColor: theme.palette.primary.main,
    };
  }

  // Saving as draft (orange/warning)
  if (isDraft) {
    return {
      label: strSaveDraft,
      mainColor: theme.palette.warning.main,
    };
  }

  // Saving as live and published (green/success)
  if (!isNoPublish && !isDraft) {
    return {
      label: strSavePublish,
      mainColor: theme.palette.success.main,
    };
  }

  // Save Changes - default (blue/primary)
  return {
    label: strSaveChanges,
    mainColor: theme.palette.primary.main,
  };
}

/**
 * Checks minimum basic requirements to save asset
 * - Admins - bypass any restrictions
 * - Checks for Modify Assets permission
 * - Checks if user in current process group
 *
 * This can be used to layer on additional checks
 * for areas that require more specific actions eg:
 * This check + read only mode for NXT UI...
 * This check + change pub status permissions...
 * This check + delete permissions...
 *
 * Note: the asset config context readOnlyState also has
 * a "message" property that can be used if needed as well
 * but should be separate from this check as it is primarily
 * trying to check for API level save permissions.
 *
 */

type GetBasicSavePermsArgs = {
  t: TFunction;
  inCurrentProcessGroup: boolean;
  sessionConfig?: TSessionConfigData;
};

type GetBasicSavePermsResult = {
  disabled: boolean; // if true: user cannot save
  isAdmin: boolean; // if was admin bypassed
  info?: string; // reasoning for disabled
};

export function getBasicSavePerms({
  t,
  inCurrentProcessGroup,
  sessionConfig,
}: GetBasicSavePermsArgs): GetBasicSavePermsResult {
  // Permissions checks
  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userCanModifyAssets, name: modifyAssetPermName } =
    checkModifyAssetsPermission(sessionConfig);

  // Translation strings
  const strWfRestricted = t('blox.editorial.metadata.group.workflow.help.restricted');
  const strReqModifyPerm = t('blox.permissions.msg.required_short', {
    name: modifyAssetPermName,
  });

  // Determine states and tooltip
  // - Missing modify assets permission
  // - Restricted by workflow group
  // Save API will reject if missing either of these:
  // - Must have modify asset permissions
  // - Must be in the current process group

  // Admin bypass - allowed to save
  if (userIsAdmin) {
    return {
      disabled: false,
      info: undefined,
      isAdmin: userIsAdmin,
    };
  }

  // Check modify assets permission
  if (!userCanModifyAssets) {
    return {
      disabled: true,
      info: strReqModifyPerm,
      isAdmin: userIsAdmin,
    };
  }

  // Check any workflow group restrictions
  // Note: inCurrentProcessGroup via asset config context
  //  -- true if no workflow / no proc group
  //  -- else checks users group strings vs process...
  if (!inCurrentProcessGroup) {
    return {
      disabled: true,
      info: strWfRestricted,
      isAdmin: userIsAdmin,
    };
  }

  // All checks passed - allowed to save
  return {
    disabled: false,
    info: undefined,
    isAdmin: userIsAdmin,
  };
}

/**
 * Minimal structural type covering both FullEditorialAsset and
 * EditorialAssetSearchResultItem — both have optional status and delete_time.
 */
type AssetDeleteCheckFields = {
  status?: string | null;
  delete_time?: string | null;
};

/**
 * Returns true if the asset is actually deleted:
 * - status is 'deleted', OR
 * - delete_time is set and the deletion moment has already passed
 *
 * An asset with a delete_time in the FUTURE is "scheduled for deletion" but
 * NOT yet deleted. All dates from the API are UTC; siteTimeZone is used for
 * consistent local-time comparison.
 */
export function isAssetActuallyDeleted(
  item: AssetDeleteCheckFields | undefined,
  siteTimeZone?: string,
): boolean {
  if (!item){
    return false;
  }
  if (item.status === 'deleted'){
    return true;
  }

  const { delete_time } = item;
  if (!delete_time){
    return false;
  }

  const deleteAt = siteTimeZone
    ? dayjs.utc(delete_time).tz(siteTimeZone)
    : dayjs.utc(delete_time);
  const now = siteTimeZone ? dayjs().tz(siteTimeZone) : dayjs.utc();

  return deleteAt.isValid() && deleteAt.isBefore(now);
}
